import { faArrowsRepeat, faCircleInfo, faFileSlash } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useState } from 'react'
import { ButtonPrimary, ButtonRed } from '../../../../../../../../../../baseComponents/Button'
import TwinIcon, { TwinIconToolTip } from '../../../../../../../../../../baseComponents/TwinIcon'
import { InputTableURLsTable } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import TwinTrans from '../../../../../../../../../../baseComponents/TwinTrans'
import { SellTicketButtonsProps, SetTPVCompleteDataTy, TPVOrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellTicketTPVLayout/types'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'
import { ModalOpenedTy } from '../../../../../../../../../../baseComponents/Modal/types'
import PermissionChecker from '../../../../../../../../../../baseComponents/PermissionChecker'
import { getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import ModaCreateSellTicketRefundConnect from '../ModalCreateSellTicketRefund'
import { parsedTicketTableData } from '../../../../../functions'
import { sellTicketStatusInversed, sellTicketTypeInversed } from '@teinor/erp/types/company/order/sell/sellTicket'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'

interface SellTicketFinishedStepButtonsProps extends SellTicketButtonsProps { 
    inputTableURLs: InputTableURLsTable
    completeData: TPVOrderData
    setCompleteData: SetTPVCompleteDataTy
}

const SellTicketFinishedStepButtons: React.FC<SellTicketFinishedStepButtonsProps> = ({ ...rest }) => {
    const showRefundButton = rest.completeData?.header?.status !== sellTicketStatusInversed['refunded'] && rest.completeData?.header?.type !== sellTicketTypeInversed['partialCharged']
    return (
        <div className='flex flex-row bg-white product_ticket_buttons overflow-hidden'>
            <RestartSellTicketButton {...{ fullButton: !showRefundButton, ...rest }} />
            {showRefundButton ? <RefundSellTicketButton {...rest} /> : null}
        </div>
    )
}

interface RefundSellTicketButtonProps extends RefundSellTicketButtonLogicProps {}

const RefundSellTicketButton: React.FC<RefundSellTicketButtonProps> = ({ ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleSellTicketRefundProcess, opened, setOpened, handleOnSubmitProcessSellTicketRefund } = useRefundSellTicketButtonLogic({ ...rest })
    let loading = false
    const today = new Date()
    const canNotRefundDate = rest.completeData?.header?.maxRefundDate && (today.getTime() > new Date(rest.completeData?.header?.maxRefundDate).getTime())

    return (
        <PermissionChecker permission='create' userPermissions={getUserPermissions('sell.tickets')}>
            <ButtonRed className={'flex-auto flex items-center justify-center ticket_button_border_right  w-1/2 ' + (canNotRefundDate ? '' : 'red_ticket_button_parent')} onClick={handleSellTicketRefundProcess} disabled={canNotRefundDate}>
                <TwinIcon icon={faFileSlash} className={'mr-8 text-white regular20 ' + (loading ? 'hidden' : '')} />
                <span>{loading ?
                    <TwinTrans transKey='processingDotsv2' >Procesando...</TwinTrans>
                    : <TwinTrans transKey='createRefund'>Crear devolución</TwinTrans>}
                </span>
                {canNotRefundDate ? <TwinIconToolTip message={t('notSellTicketRefundDate', 'El ticket no está en plazo de devolución')} id={'notRefund'} icon={faCircleInfo} className='ml-20' classNameIcon='w-18 h-18' /> : null}
            </ButtonRed>
            {opened ? <ModaCreateSellTicketRefundConnect setOpened={setOpened} onSubmit={() => { }} {...{ handleOnSubmitProcessSellTicketRefund, ...rest }} /> : null}
        </PermissionChecker>
    )
}

interface RefundSellTicketButtonLogicProps extends SellTicketFinishedStepButtonsProps {}

const useRefundSellTicketButtonLogic = ({ setCompleteData}: RefundSellTicketButtonLogicProps) => {
    const [opened, setOpened] = useState<ModalOpenedTy>(null)
    const handleSellTicketRefundProcess = useCallback(async () => {
        setOpened(true)
    }, [setOpened])
    
    const handleOnSubmitProcessSellTicketRefund = useCallback(async () => {
        setCompleteData((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.lastProductIndex = null
            copyOld.header = {}
            copyOld.tableData = []
            delete copyOld.selectedTableData
            copyOld.Customer = {}
            parsedTicketTableData(copyOld)
            return copyOld
        })
    }, [setCompleteData])
    return { opened, setOpened, handleSellTicketRefundProcess, handleOnSubmitProcessSellTicketRefund }
}

interface RestartSellTicketButtonProps extends RestartSellTicketButtonLogicProps { 
    fullButton: boolean
}

const RestartSellTicketButton: React.FC<RestartSellTicketButtonProps> = ({ fullButton, ...rest }) => {
    const { loading, handleRestartSellTicket } = useRestartSellTicketButtonLogic({ ...rest })
    return (
        <ButtonPrimary className={'flex-auto flex items-center justify-center charge_ticket_button_parent w-1/2 ' + (fullButton ? 'ticket_button_border' : 'ticket_button_border_left ')}onClick={handleRestartSellTicket}>
            <TwinIcon icon={faArrowsRepeat} className={'mr-8 text-white regular20 ' + (loading ? 'hidden' : '')} />
            <span>{loading ?
                <TwinTrans transKey='processingDotsv2' >Procesando...</TwinTrans>
                : <TwinTrans transKey='newTicket'>Nuevo ticket</TwinTrans>}
            </span>
        </ButtonPrimary>
    )
}

interface RestartSellTicketButtonLogicProps extends SellTicketFinishedStepButtonsProps {
    setCompleteData: SetTPVCompleteDataTy
}

const useRestartSellTicketButtonLogic = ({ setCompleteData }: RestartSellTicketButtonLogicProps) => {
    const { endLoading, loading, startLoading } = useIsLoading()
    const handleRestartSellTicket = useCallback(async () => {
        startLoading?.()
        setCompleteData((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.lastProductIndex = null
            copyOld.header = {}
            copyOld.tableData = []
            delete copyOld.selectedTableData
            copyOld.Customer = {}
            parsedTicketTableData(copyOld)
            return copyOld
        })
        endLoading?.()
    }, [startLoading, endLoading, setCompleteData])
    return { loading, handleRestartSellTicket }
}

export default SellTicketFinishedStepButtons